import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Badge, Card } from '@safetyhub/ui';
import { agencyOverlayFormName, formatDateToLocal } from '@safetyhub/shared';
import { apiErrorMessage } from '../../lib/api';
import { getLegalDocumentImminent, legalDocumentsQueryKeys } from '../../lib/legalDocuments';
import {
  describeImminentCounts,
  groupImminentByObligation,
  INSTANCE_STATUS_LABEL,
} from '../../lib/legalDocumentLabels';
import { legalSiteObligationsPath } from '../../lib/legalDocumentRoutes';

/**
 * 기관 전체 「기한이 다가오는 문서」 — 스토리 L1(「내가 맡은 사업장 **전체**에서 기한이 다가오거나 지난 의무를 한
 * 화면에서 본다」). 분모가 현장 하나가 아니라 **보는 사람이 접근하는 전체**라 현장 선택 화면 위에 둔다
 * (스토리 기대 — 「기관은 목록 우선, 업체는 한 사업장 우선」).
 *
 * 무엇을 싣는지(창 = 그 현장 시간대의 이번 달 말과 임박 알림 창의 끝 중 늦은 쪽 · 지연 포함 · 문서가 붙은 것·제외·
 * 적용 어긋남은 빠짐 — 줄기 9-u: 알림이 간 문서는 여기 반드시 있다)는 서버가 정한다
 * (`LegalDocumentsService.listImminent` docblock). 이 컴포넌트는 그 행을 **문서명(의무)별로 묶어** 그린다(사용자 결정
 * 2026-10-29) — 묶음 머리에 상태별 건수, 그 아래 현장 줄. 묶음·순서 규칙은 `groupImminentByObligation`이 소유한다
 * (서버 순서 보존 — 묶음은 첫 출현 순이라 지연 묶음이 앞, 묶음 안은 기한 오름차순).
 *
 * 호출부가 `obligation.view`로 가린다 — 그 키가 없으면 서버가 403이라 패널을 띄우지 않는다.
 */
export default function ImminentPanel({ companyId }: { companyId: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: legalDocumentsQueryKeys.imminent(companyId),
    queryFn: () => getLegalDocumentImminent(companyId),
  });

  const items = data?.items ?? [];

  return (
    <section aria-labelledby="legal-imminent-heading" className="mb-6">
      <h2 id="legal-imminent-heading" className="mb-2 text-[17px] font-extrabold text-ink-900">
        기한이 다가오는 문서
      </h2>
      {isLoading ? (
        <div
          className="flex justify-center py-6"
          role="status"
          aria-label="기한이 다가오는 문서 불러오는 중"
        >
          <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-brand-600" />
        </div>
      ) : error ? (
        <div role="alert" className="rounded-card border border-rose-200 bg-rose-50 p-3">
          <p className="text-sm font-medium text-rose-700">
            {apiErrorMessage(error) ?? '기한이 다가오는 문서를 불러오지 못했습니다.'}
          </p>
        </div>
      ) : items.length === 0 ? (
        <Card padding="none" radius="lg" elevation="md" className="px-4 py-5 text-sm text-ink-500">
          기한이 다가오거나 지난, 아직 등록되지 않은 문서가 없습니다.
        </Card>
      ) : (
        <Card padding="none" radius="lg" elevation="md" className="overflow-hidden">
          <ul className="divide-y divide-ink-200" aria-label="기한이 다가오는 문서 목록">
            {groupImminentByObligation(items).map((group) => {
              const headingId = `legal-imminent-group-${group.obligationId}`;
              return (
                <li key={group.obligationId} aria-labelledby={headingId} className="py-2">
                  <div className="flex items-baseline gap-2 px-4 pt-1 sm:px-6">
                    <p id={headingId} className="min-w-0 truncate text-sm font-bold text-ink-900">
                      {group.title}
                    </p>
                    <p className="flex-none text-xs font-medium text-ink-500">
                      {describeImminentCounts(group.counts)}
                    </p>
                  </div>
                  <ul aria-label={`${group.title} 현장`}>
                    {group.items.map((item) => {
                      const { label, tone } = INSTANCE_STATUS_LABEL[item.instance.status];
                      // 묶음 이름은 공통 이름 그대로 — 대행 현장 줄에만 기관 서식 이름(사용자 결정 2026-10-30)
                      const agencyFormName = agencyOverlayFormName(item.agencyOverlay, item.title);
                      return (
                        <li key={item.instance.instanceId}>
                          <Link
                            to={legalSiteObligationsPath(item.site.siteId, item.obligationId)}
                            className="flex items-center gap-3 py-1.5 pl-8 pr-4 hover:bg-surface-hover sm:pl-10 sm:pr-6"
                          >
                            <p className="min-w-0 flex-1 truncate text-xs text-ink-700">
                              {item.site.siteName}
                              {agencyFormName !== null && ` · 기관 서식: ${agencyFormName}`} · 기한{' '}
                              {formatDateToLocal(new Date(item.instance.deadline))}
                            </p>
                            <Badge tone={tone} size="sm" dot>
                              {label}
                            </Badge>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </li>
              );
            })}
          </ul>
        </Card>
      )}
    </section>
  );
}
